import React from 'react';
import { EmailTemplate } from '../types';

interface InviteEmailData {
  inviteeName?: string;
  inviterName: string;
  inviteLink: string;
  role?: string;
  department?: string;
  expiresIn?: string;
  organizationName?: string;
}

export const InviteEmailTemplate: EmailTemplate<InviteEmailData> = {
  id: 'invite',
  name: 'User Invitation Email',
  subject: ({ inviterName, organizationName }) => 
    `${inviterName} invited you to join ${organizationName || 'our app'}`,
  preview: 'You have been invited to join our platform',
  body: {
    html: (data) => `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Invitation</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 600px;
              margin: 0 auto;
              padding: 20px;
            }
            .header {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 40px 30px;
              text-align: center;
              border-radius: 8px 8px 0 0;
            }
            .content {
              background-color: #ffffff;
              padding: 40px 30px;
              border: 1px solid #e2e8f0;
              border-radius: 0 0 8px 8px;
            }
            .role-badge {
              display: inline-block;
              padding: 6px 12px;
              background-color: #e2e8f0;
              color: #4a5568;
              border-radius: 4px;
              font-weight: 500;
              margin: 5px 0;
            }
            .button {
              display: inline-block;
              padding: 14px 28px;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              text-decoration: none;
              border-radius: 6px;
              font-weight: 500;
              margin: 20px 0;
            }
            .expiry-notice {
              background-color: #fff5f5;
              border-left: 4px solid #fc8181;
              padding: 12px;
              margin: 20px 0;
              font-size: 14px;
            }
            .footer {
              text-align: center;
              margin-top: 40px;
              color: #718096;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>You're Invited!</h1>
          </div>
          <div class="content">
            <p>Hi${data.inviteeName ? ` ${data.inviteeName}` : ''},</p>
            <p><strong>${data.inviterName}</strong> has invited you to join <strong>${data.organizationName || 'our platform'}</strong>.</p>
            
            ${data.role || data.department ? `
              <p>You've been invited with the following access:</p>
              ${data.role ? `<span class="role-badge">Role: ${data.role}</span><br>` : ''}
              ${data.department ? `<span class="role-badge">Department: ${data.department}</span>` : ''}
            ` : ''}
            
            <p>Click the button below to accept the invitation and create your account:</p>
            
            <div style="text-align: center;">
              <a href="${data.inviteLink}" class="button">Accept Invitation</a>
            </div>
            
            <div class="expiry-notice">
              <strong>⏰ Note:</strong> This invitation will expire ${data.expiresIn || 'in 7 days'}. 
              Please accept it before then.
            </div>
            
            <p>If you have any questions about this invitation, please contact ${data.inviterName} or our support team.</p>
            
            <p>Best regards,<br>The ${data.organizationName || 'Team'}</p>
          </div>
          <div class="footer">
            <p>If you didn't expect this invitation, you can safely ignore this email.</p>
            <p>© 2024 ${data.organizationName || 'Your Company'}. All rights reserved.</p>
          </div>
        </body>
      </html>
    `,
    text: (data) => `
You're Invited!

Hi${data.inviteeName ? ` ${data.inviteeName}` : ''},

${data.inviterName} has invited you to join ${data.organizationName || 'our platform'}.

${data.role || data.department ? `You've been invited with the following access:
${data.role ? `- Role: ${data.role}` : ''}
${data.department ? `- Department: ${data.department}` : ''}` : ''}

Accept the invitation: ${data.inviteLink}

⏰ Note: This invitation will expire ${data.expiresIn || 'in 7 days'}. Please accept it before then.

If you have any questions about this invitation, please contact ${data.inviterName} or our support team.

Best regards,
The ${data.organizationName || 'Team'}

If you didn't expect this invitation, you can safely ignore this email.
© 2024 ${data.organizationName || 'Your Company'}. All rights reserved.
    `.trim(),
  },
};

export function InviteEmail(props: InviteEmailData) {
  return (
    <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: 600, margin: '0 auto' }}>
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '40px 30px',
        textAlign: 'center',
        borderRadius: '8px 8px 0 0',
      }}>
        <h1>You're Invited!</h1>
      </div>
      <div style={{
        backgroundColor: '#ffffff',
        padding: '40px 30px',
        border: '1px solid #e2e8f0',
        borderRadius: '0 0 8px 8px',
      }}>
        <p>Hi{props.inviteeName ? ` ${props.inviteeName}` : ''},</p>
        <p><strong>{props.inviterName}</strong> has invited you to join <strong>{props.organizationName || 'our platform'}</strong>.</p>
        
        {(props.role || props.department) && (
          <>
            <p>You've been invited with the following access:</p>
            {props.role && (
              <span style={{
                display: 'inline-block',
                padding: '6px 12px',
                backgroundColor: '#e2e8f0',
                color: '#4a5568',
                borderRadius: 4,
                fontWeight: 500,
                margin: '5px 0',
              }}>
                Role: {props.role}
              </span>
            )}
            {props.role && props.department && <br />}
            {props.department && (
              <span style={{
                display: 'inline-block',
                padding: '6px 12px',
                backgroundColor: '#e2e8f0',
                color: '#4a5568',
                borderRadius: 4,
                fontWeight: 500,
                margin: '5px 0',
              }}>
                Department: {props.department}
              </span>
            )}
          </>
        )}
        
        <p>Click the button below to accept the invitation and create your account:</p>
        
        <div style={{ textAlign: 'center' }}>
          <a 
            href={props.inviteLink} 
            style={{
              display: 'inline-block',
              padding: '14px 28px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: 6,
              fontWeight: 500,
              margin: '20px 0',
            }}
          >
            Accept Invitation
          </a>
        </div>
        
        <div style={{
          backgroundColor: '#fff5f5',
          borderLeft: '4px solid #fc8181',
          padding: 12,
          margin: '20px 0',
          fontSize: 14,
        }}>
          <strong>⏰ Note:</strong> This invitation will expire {props.expiresIn || 'in 7 days'}. 
          Please accept it before then.
        </div>
        
        <p>If you have any questions about this invitation, please contact {props.inviterName} or our support team.</p>
        
        <p>Best regards,<br />The {props.organizationName || 'Team'}</p>
      </div>
      <div style={{ textAlign: 'center', marginTop: 40, color: '#718096', fontSize: 14 }}>
        <p>If you didn't expect this invitation, you can safely ignore this email.</p>
        <p>© 2024 {props.organizationName || 'Your Company'}. All rights reserved.</p>
      </div>
    </div>
  );
}